/**
 * spaceHudSnapshot.ts
 * Read-only space HUD data snapshot for src/game/ui/SpaceHUD.
 *
 * ARCHITECTURE (ADR-008, mirrors hudSnapshot.ts):
 *   This module lives in src/game/engine/ and provides a dedicated
 *   read-only boundary for the space HUD's displayed values:
 *   - `stepSpace` writes the snapshot each tick (engine side)
 *   - SpaceHUD.tsx reads from the snapshot via RAF polling (UI side)
 *
 *   AGENTS.md "Folder Rules" forbid `src/game/ui/` from reading engine
 *   state directly. The platformer HUD already pipes through
 *   `hudSnapshot.ts`; the space HUD now uses the same pattern so the
 *   boundary is honoured for both scenes (PR #124 review).
 *
 * Phase 4 — World 2: Starfall Drift.
 *
 * @module spaceHudSnapshot
 */

/** Shape of the space HUD data snapshot. */
export interface SpaceHudSnapshot {
  readonly health: number;
  readonly maxHealth: number;
  readonly levelName: string;
}

/**
 * Module-scoped mutable snapshot object.
 * DECISION: Mutate fields in-place rather than replacing the object
 * each tick to avoid allocating a new object at 60fps (reduces GC
 * churn) — same pattern as `hudSnapshot.ts`.
 * @internal
 */
const snapshot: {
  health: number;
  maxHealth: number;
  levelName: string;
} = {
  health: 0,
  maxHealth: 0,
  levelName: '',
};

/**
 * Update the space HUD snapshot in place. Called from `stepSpace` each
 * tick. Mutates fields of the module-scoped object to avoid per-tick
 * allocations.
 *
 * @param health    - Current ship HP.
 * @param maxHealth - Ship's max HP.
 * @param levelName - Display name of the active space level.
 */
export function updateSpaceHudSnapshot(
  health: number,
  maxHealth: number,
  levelName: string,
): void {
  snapshot.health = health;
  snapshot.maxHealth = maxHealth;
  snapshot.levelName = levelName;
}

/**
 * Get the current space HUD snapshot for UI rendering.
 * Returns the mutable module-scoped object typed as readonly.
 * WARNING: Do not retain references long-term — fields are mutated
 * each tick.
 *
 * @returns Current space HUD values snapshot (typed readonly but
 *          mutated in place).
 */
export function getSpaceHudSnapshot(): Readonly<SpaceHudSnapshot> {
  return snapshot;
}

/**
 * Reset the space HUD snapshot to initial values.
 * Called from `initSpaceState()` and `clearSpaceState()` so a new
 * space session never paints the previous session's hearts / level
 * name on the HUD's first read (before `stepSpace` writes the new
 * tick). Also used directly by tests.
 */
export function resetSpaceHudSnapshot(): void {
  snapshot.health = 0;
  snapshot.maxHealth = 0;
  snapshot.levelName = '';
}
